import technicalDomain from './technical-domain.png'
import dependenciesInversion from './dependencies-inversion.png'

const content = `<p>Hexagonal architecture is a useful pattern but with a very unfortunate name. Indeed, it has nothing to do with hexagons.</p>

<p>The idea of Hexagonal architecture is that <strong>domain code should not change for technical reasons</strong>.</p>

<p>In practice, this means removing all trace of technical concerns in domain code. It consists in two things:</p>

<ul><li>separating domain code and technical code</li><li>making technical code depend on domain code and not the other way around</li></ul>

<p>Let’s see what this means in detail.</p>

<h2 id="Domain-code-and-technical-code">Domain code and technical code</h2>

<p>There are typically two kinds of concerns in a codebase: technical concerns and domain concerns.</p>

<h3 id="Technical-concerns">Technical concerns</h3>

<p>Technical concerns are the technology that supports your application. This includes:</p>

<ul><li>the fact that the application is a piece of software installed on the user’s machine</li><li>or the fact that it’s a web application, that handles HTTP endpoints and routes</li><li>the fact that the backend returns a JSON payload to the frontend</li><li>the fact that it reads and stores information in cookies</li><li>the fact that the application uses a framework such as Rails, Django, Spring, etc.</li><li>the fact that the application uses a database</li><li>the particular database system: PG, SQLite, Oracle, etc.</li><li>the structure of data storage and the ORM: Active Record, Hibernate, Entity Framework, etc.</li><li>the fact that the application uses some form of caching</li><li>the in memory caches used: Redis, etc.</li><li>the fact that it reads environment variables</li><li>the fact there is a GUI</li><li>the fact that the application can be used in the console</li><li>the fact that the code reads and write information from files</li><li>the fact that the application uses external services for payment, shipping, authentication, etc.</li><li>and the list goes on.</li></ul>

<p>Technical concerns are common to many applications.</p>

<h3 id="Domain-concerns">Domain concerns</h3>

<p>Domain concerns, also called business logic or business rules, are typically the <strong>topics that you discuss with your Product team</strong>. Contrary to technical concerns, domain concerns are specific to your application. For example:</p>

<ul><li>how to compute the price of a product</li><li>what happens when a user deletes their account</li><li>for booking applications, how is overbooking handled</li><li>what the various types of users have the right to access to</li><li>how to choose the suggestions a user might like</li><li>and the list goes on, with all the specificities of each application.</li></ul>

<h3 id="Separating-domain-and-technical-concerns">Separating domain and technical concerns</h3>

<p>Hexagonal architecture starts with having a clear separation between those two types of concerns: the code that implements the domain lives in some files, and the technical concerns in other files.</p>

<p>Let’s take an example to illustrate. An e-commerce website needs to display the price of a given product. The selling price is computed like this: it is the purchase price + a margin that depends on the product category: books have a margin of 5%, consumer electronics have a margin of 2%, and the other products have a margin of 3%.</p>

<p>The frontend calls the backend on a dedicated HTTP endpoint to retrieve the price of the product.</p>

<p>If we break down this requirement into domain concerns and technical concerns we have:</p>

<ul><li>domain concerns:<ul><li>determining what inputs contribute to the price</li><li>computing the price</li></ul></li><li>technical concerns:<ul><li>querying the database</li><li>formatting an HTTP answer</li></ul></li></ul>

<img src=${technicalDomain} alt="technical-domain" />

<p>In Rails, the HTTP request would end up in a controller. The controller calls the price calculator and formats its response:</p>

<pre><code class="language-ruby">class PricesController &lt; ApplicationController
  def show
    product_id = params&#91;:product_id] # coming from the HTTP request
    price = PriceCalculator.new.compute(product_id)
    
    render json: { price: price }
  end
end</code></pre>

<p>On domain side, the price calculator computes the price. To obtain the data it needs, the calculator calls another class that we can call “repository”. From the calculator’s perspective, the repository doesn’t show its source of data (database, file, etc.). It just exposes an interface to provide that data:</p>

<pre><code class="language-ruby">class PriceCalculator
  def compute(product_id)
    repository = ProductRepository.new
    product = repository.get_product(product_id)
    margin = case product.category
      when 'book' then 0.05
      when 'consumer electronics' then 0.02
      else 0.03
    end
    product.purchase_price * (1 + margin)
  end
end</code></pre>

<p>Back to technical side, the repository takes care of querying the database. The products table contains a foreign key to the categories tables, that contains itself the label of the category:</p>

<pre><code class="language-ruby">class ProductRepository
  def get_product(product_id)
    Product
      .joins(:category)
      .where('products.id = ?', product_id)
      .select(:purchase_price, 'categories.label')
      .first
  end
end</code></pre>

<h3 id="The-advantages-of-separating-technical-code-from-domain-code">The advantages of separating technical code from domain code</h3>

<p>The first advantage of this separation is that every part of the code is small and simple. In the above example, we cut up the code into 3 different parts: the controller to handle the HTTP request, the calculator for the business logic and the repository to fetch the data. This made each part 3 times smaller and simpler than if all the code were at the same place in the controller.</p>

<p>The bigger the application, the bigger difference it makes. In an application where we keep adding new features, this makes a significant difference over time.</p>

<p>Another advantage is that the repository and calculator can be reused in different contexts. For example, to compute the price in a batch script instead of an HTTP request, the calculator can be reused as is. Similarly, to retrieve product categories for another algorithm, the product repository can be reused as is.</p>

<p>Yet another advantage is that if the structure of the data storage changes, for example the label of the category gets moved to another table, it’s only the <code>ProductRepository</code> that has to change, and not all places in the domain code that use this data. This contributes to the central idea of Hexagonal architecture that the <strong>domain code should not change for technical reasons</strong>.</p>

<p>Another advantage of using a repository is to have a uniform way to access the data for a type of resource across all the domain code.</p>

<h2 id="Making-technical-code-depend-on-domain-code">Making technical code depend on domain code</h2>

<p>There is still one kind of technical reason that could make the domain code change: if we want to use another kind of storage: for example a cache instead of a database, or a mix of both.</p>

<p>In this case, our price calculator has to change to make it call another source of data. But we don’t want to change our domain code for technical reasons. To avoid this, we can pass the source of data to the domain code, so that it doesn’t know what this source is exactly:</p>

<pre><code class="language-ruby">class PriceCalculator
  def initialize(product_repository) # the source of data is now passed in
    @repository = product_repository 
  end
  
  def compute(product_id)
    product = @repository.get_product(product_id)
    margin = case product.category
      when 'book' then 0.05
      when 'consumer electronics' then 0.02
      else 0.03
    end
    product.purchase_price * (1 + margin)
  end
end</code></pre>

<p>And it is its caller, which is on the technical side of the code, that determines the source of data. We can rename our <code>ProductRepository</code> class that fetch data from the database into <code>ProductDBRepository</code> class. This lets us create other repositories, such as <code>ProductCacheRepository</code> that fetches the data from a cache:</p>

<pre><code class="language-ruby">class PricesController &lt; ApplicationController
  def show
    product_id = params&#91;:product_id]
    price = PriceCalculator.new(ProductCacheRepository.new).compute(product_id)
    
    render json: { price: price }
  end
end</code></pre>

<p>We can now freely pass different sources of data (including test data) to the price calculator without having to change its code. That prevents it from varying for technical reasons.</p>

<p>An example of source of data to pass is data defined in tests. This lets tests call the domain code without relying on a database.</p>

<h3 id="A-bit-of-terminology">A bit of terminology</h3>

<p>This technique of passing in the class to call is called a dependency injection (“injection” as in we stick the object in the calculator), and it achieves an inversion of dependencies.</p>

<p>It’s an inversion of dependencies because the calculator no longer depends on the repository’s class, as it no longer knows the name of that class. It just knows that the repository object it’s been injected will provide whatever methods it needs. It’s rather the repository that depends on the calculator, because the repository knows what the calculator needs, and provides it.</p>

<p>It’s interesting to note that, with inversion of dependencies, the direction of dependencies is not always the same as the one of code execution:</p>

<img src=${dependenciesInversion} alt="dependencies-inversion" />

<p>The consideration of who depends on who is even more explicit in typed languages where the repository implements an interface that is defined in domain code.</p>

<div class="notes">
<p>Key points:</p>

<ul><li>the code of an application is made of domain concerns and technical concerns</li><li>separating the two makes code simpler and more flexible</li><li>the domain code should not change for technical reasons</li><li>dependencies inversion allows to make technical code depend on domain code</li></ul>
</div>`

export default content
